import { getHSLAColor } from "@/helpers/style.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiEmployeeShort, getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiParticipantChartAxisType,
  ApiParticipantChartConfigAxisConfig,
  ApiParticipantChartDataPointSize,
  ApiPerformanceReviewParticipantChart,
  ApiPerformanceReviewReviewerTypes,
} from "@/types/performance-review.types.ts";
import { Box } from "@skailer-com/uikit";
import {
  BubbleDataPoint,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  Plugin,
  PointElement,
  Title as TitleChart,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bubble } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

const pointRadiusMap: Record<ApiParticipantChartDataPointSize, number> = {
  [ApiParticipantChartDataPointSize.smallest]: 25,
  [ApiParticipantChartDataPointSize.small]: 35,
  [ApiParticipantChartDataPointSize.medium]: 45,
  [ApiParticipantChartDataPointSize.large]: 55,
  [ApiParticipantChartDataPointSize.largest]: 65,
};

export interface ChartPoint extends BubbleDataPoint {
  employee_ids: number[];
  employee?: ApiEmployeeShort;
}

export interface PerformanceReviewInsightChartProps {
  chart: ApiPerformanceReviewParticipantChart;
  onClickBubble: (employee_ids: number[]) => void;
}

export const PerformanceReviewInsightChart = ({
  chart,
  onClickBubble,
}: PerformanceReviewInsightChartProps) => {
  ChartJS.register(LinearScale, PointElement, Tooltip, Legend, TitleChart, ChartDataLabels);
  const { t } = useTranslation();
  const { points, x_axis, y_axis } = chart.data;
  const { renderEmployeeName } = useFormatter();

  const maxXValue = x_axis.max;
  const minXValue = x_axis.min;
  const maxYValue = y_axis.max;
  const minYValue = y_axis.min;

  const xRange = maxXValue - minXValue;
  const yRange = maxYValue - minYValue;
  const paddingRatio = 0.1;
  const paddingX = xRange * paddingRatio;
  const paddingY = yRange * paddingRatio;
  const minX = minXValue - paddingX;
  const maxX = maxXValue + paddingX;
  const minY = minYValue - paddingY;
  const maxY = maxYValue + paddingY;

  const data: ChartPoint[] = points.map((point): ChartPoint => {
    const radius = pointRadiusMap[point.size];
    return {
      ...point,
      r: radius,
    };
  });

  const chartDatasets: ChartData<"bubble"> = {
    datasets: [
      {
        data,
        backgroundColor: getHSLAColor("--color-brand-dark", 0.6),
        hoverBackgroundColor: getHSLAColor("--color-brand-dark", 0.9),
        parsing: false,
        radius: (ctx) => {
          if (!ctx.raw) return;
          const point = ctx.raw as ChartPoint;
          return point.r ?? pointRadiusMap[ApiParticipantChartDataPointSize.smallest];
        },
        hoverRadius: (ctx) => {
          if (!ctx.raw) return;
          const point = ctx.raw as ChartPoint;
          return ((point.r ?? pointRadiusMap[ApiParticipantChartDataPointSize.smallest]) / 2) * 1.2;
        },
      },
    ],
  };

  const options: ChartOptions<"bubble"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { autoPadding: false },
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: {
        display: (ctx) => ctx.active,
        formatter: (value: ChartPoint) => {
          function splitToTwoLines(name: string): string {
            const [first, second] = name.split(" ");
            return `${first}${second ? `\n${second}` : ""}`;
          }

          function generateAcronym(text: string): string {
            const words = text.toUpperCase().trim().split(/\s+/).filter(Boolean);
            if (words.length >= 2) {
              return words[0][0] + words[1][0];
            }
            return words[0]?.slice(0, 2) || "";
          }

          if (value.employee) {
            if (value.employee.avatar_url) return "";

            const name = renderEmployeeName(value.employee);
            return name.length > 15 ? generateAcronym(name) : splitToTwoLines(name);
          }

          const countLabel = t("plurals:people", { count: value.employee_ids.length });
          return splitToTwoLines(countLabel);
        },

        font: (ctx) => {
          const point = ctx.dataset.data[ctx.dataIndex] as ChartPoint;
          const baseSize = 14;

          if (point.employee) {
            const name = renderEmployeeName(point.employee);
            return {
              weight: "bold",
              size:
                Math.round(Math.max(name.length > 15 ? baseSize : 16 - name.length / 3, 10) * 10) /
                10,
            };
          } else if (point.r === pointRadiusMap[ApiParticipantChartDataPointSize.smallest]) {
            return { weight: "bold", size: 12 };
          }

          return { weight: "bold", size: baseSize };
        },

        color: "#fff",
        align: "center",
        anchor: "center",
        textAlign: "center",
      },
      tooltip: { enabled: false },
    },
    scales: {
      x: {
        display: false,
        min: minX,
        max: maxX,
      },
      y: {
        display: false,
        min: minY,
        max: maxY,
      },
    },
    onClick: (_, elements) => {
      if (!elements?.length) return;
      const { datasetIndex, index } = elements[0];
      const point = (chartDatasets.datasets[datasetIndex].data as ChartPoint[])[index];
      onClickBubble(point.employee_ids);
    },
    onHover: (e, elements) => {
      (e.native?.target as HTMLCanvasElement).style.cursor = elements?.length
        ? "pointer"
        : "default";
    },
  };

  const gartnerQuadrantPlugin: Plugin<"bubble"> = {
    id: "gartnerQuadrant",
    beforeDraw: (chartData) => {
      const getSquareAxisTitle = (
        config: ApiParticipantChartConfigAxisConfig,
        isGood: boolean
      ): string => {
        if (config.type === ApiParticipantChartAxisType.overallReview) {
          return isGood
            ? t("performance_review:chart_performance_good")
            : t("performance_review:chart_performance_lower");
        }

        if (config.type === ApiParticipantChartAxisType.reviewerType && config.reviewer_type) {
          switch (config.reviewer_type.type) {
            case ApiPerformanceReviewReviewerTypes.myself:
              return isGood
                ? t("performance_review:chart_title_good_self")
                : t("performance_review:chart_title_lower_self");
            case ApiPerformanceReviewReviewerTypes.manager:
              return isGood
                ? t("performance_review:chart_title_good_manager")
                : t("performance_review:chart_title_lower_manager");
            case ApiPerformanceReviewReviewerTypes.upward:
              return isGood
                ? t("performance_review:chart_title_good_upward")
                : t("performance_review:chart_title_lower_upward");
            case ApiPerformanceReviewReviewerTypes.peer:
              return isGood
                ? t("performance_review:chart_title_good_peer")
                : t("performance_review:chart_title_lower_peer");
            case ApiPerformanceReviewReviewerTypes.custom:
              if (config.reviewer_type.title) {
                return isGood
                  ? t("performance_review:chart_title_good_custom", {
                      title: config.reviewer_type.title,
                    })
                  : t("performance_review:chart_title_lower_custom", {
                      title: config.reviewer_type.title,
                    });
              } else {
                return isGood
                  ? t("performance_review:chart_performance_good")
                  : t("performance_review:chart_performance_lower");
              }
          }
        }

        return isGood
          ? t("performance_review:chart_performance_good")
          : t("performance_review:chart_performance_lower");
      };

      const { ctx, chartArea } = chartData;
      const { top, bottom, left, width, height } = chartArea;

      const midX = left + width / 2;
      const midY = top + height / 2;

      ctx.save();

      ctx.fillStyle = getHSLAColor("--color-brand-dark", 0.05);
      ctx.fillRect(left, top, width, height);

      ctx.strokeStyle = getHSLAColor("--color-brand-dark", 0.1);
      ctx.lineWidth = 1;
      ctx.strokeRect(left, top, width / 2, height / 2);
      ctx.strokeRect(midX, top, width / 2, height / 2);
      ctx.strokeRect(left, midY, width / 2, height / 2);
      ctx.strokeRect(midX, midY, width / 2, height / 2);

      ctx.fillStyle = getHSLAColor("--color-dark", 0.5);
      ctx.font = "14px 'Inter', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const pudding = 15;

      ctx.fillText(
        getSquareAxisTitle(chart.config.x_axis, false),
        left + width * 0.25,
        bottom - pudding
      );
      ctx.fillText(
        getSquareAxisTitle(chart.config.x_axis, true),
        left + width * 0.75,
        bottom - pudding
      );
      ctx.save();
      ctx.translate(left + pudding, midY - height * 0.25);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(getSquareAxisTitle(chart.config.y_axis, true), 0, 0);
      ctx.restore();
      ctx.save();
      ctx.translate(left + pudding, midY + height * 0.25);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(getSquareAxisTitle(chart.config.y_axis, false), 0, 0);
      ctx.restore();

      ctx.restore();
    },
  };

  const avatarImageMap = new Map<string, HTMLImageElement>();
  const bubbleImagePlugin: Plugin<"bubble"> = {
    id: "bubbleImagePlugin",
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx;
      const dataset = chart.data.datasets[0];
      const meta = chart.getDatasetMeta(0);

      if (!dataset?.data || !meta?.data) return;

      for (let i = 0; i < dataset.data.length; i++) {
        const point = dataset.data[i] as ChartPoint;
        if (point.employee) {
          const avatarUrl = getEmployeeAvatarUrl(point.employee, "lg");
          if (!avatarUrl) continue;

          const element = meta.data[i];
          if (!element) continue;

          const { x, y } = element;
          const radius = element.options.radius as number;
          const size = radius * 2;

          let image = avatarImageMap.get(avatarUrl);

          if (!image) {
            image = new Image();
            image.src = avatarUrl;
            image.onload = () => chart.draw();
            avatarImageMap.set(avatarUrl, image);
            continue;
          }

          if (image.complete) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.clip();
            ctx.drawImage(image, x - radius, y - radius, size, size);
            ctx.restore();
          }
        }
      }
    },
  };

  return (
    <Box className={"h-[670px]"}>
      <Bubble
        data={chartDatasets}
        options={options}
        plugins={[gartnerQuadrantPlugin, bubbleImagePlugin]}
      />
    </Box>
  );
};
